import {
  IconBellRinging,
  IconDotsVertical,
  IconEdit,
  IconRobot,
  IconSearch,
  IconPlus,
  IconMessageCircle,
  IconKey,
  IconToggleLeft,
  IconToggleRight,
  IconGitBranch,
  IconTrash,
} from "@tabler/icons-react";
import { useContext, useEffect, useState, useCallback } from "react";
import {
  CardFooter,
  CardHeader,
  Dropdown,
  Modal,
  Spinner,
  Button,
  Form,
  Col,
} from "react-bootstrap";
import styled from "styled-components";
import Swal from "sweetalert2";
import NotificationsContext from "../contexts/NotificationsContext";
import UserContext from "../contexts/UserContext";
import AgentChat from "../jsx/components/Agents/AgentChat";
import {
  ActionsBar,
  AgentCard,
  AgentIconContainer,
  AgentJob,
  AgentName,
  CardContent,
  CardsContainer,
  Container,
  CustomDropdownItem,
  CustomDropdownMenu,
  CustomDropdownToggle,
  EditButton,
  EmptyState,
  FloatingButton,
  Header,
  LoadingContainer,
  LoadingWrapper,
  ModelBadge,
  NotificationButton,
  SearchContainer,
  SearchIcon,
  SearchInput,
  StatCard,
  StatLabel,
  StatNumber,
  StatsContainer,
  StatusDot,
  StatusIndicator,
  TestButton,
  Title,
  TitleIcon,
  VersionSelector,
} from "../jsx/components/Agents/styles";
import DefaultLoader from "../jsx/components/Loader";
import NotificationsModal from "../jsx/components/ModalData/NotificationsModal";
import NotificationService from "../services/NotificationsServices";
import {
  GetAgentsData,
  UpdateAgentApiKey,
  UpdateAgentStatus,
  UpdateAgentVersion,
} from "../services/WaBottyServices";
import useInfiniteScroll from "../jsx/components/InfiniteScroll.js";
import { useNavigate } from "react-router-dom";
import { deleteAgent } from "../services/AgentsServices.js";

export default function WhatsappAgents() {
  const navigate = useNavigate();

  const { bottyData } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);

  const [showAgentsData, setAgentsData] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAiModal, setAiShowModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [selectedAgentData, setSelectedAgentData] = useState(null);
  const [notificationConfig, setNotificationConfig] = useState({
    keyword: "",
    notificationTypes: [],
    phone: "",
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Novos estados para as funcionalidades adicionadas
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [selectedAgentForApiKey, setSelectedAgentForApiKey] = useState(null);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [isUpdatingApiKey, setIsUpdatingApiKey] = useState(false);
  const [showAgentChat, setShowAgentChat] = useState(false);
  const [selectedAgentForChat, setSelectedAgentForChat] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingVersion, setIsUpdatingVersion] = useState(false);

  // Estados para scroll infinito
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreAgents, setHasMoreAgents] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerPage = 10;

  // Configuração do scroll infinito
  const loadMoreAgents = useCallback(async () => {
    if (isLoadingMore || !hasMoreAgents || isLoading) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const res = await GetAgentsData(nextPage, itemsPerPage);

      if (res && res.length > 0) {
        setAgentsData((prev) => [...prev, ...res]);
        setCurrentPage(nextPage);

        // Se retornou menos que o esperado, não há mais itens
        if (res.length < itemsPerPage) {
          setHasMoreAgents(false);
        }
      } else {
        setHasMoreAgents(false);
      }
    } catch (error) {
      console.error("Erro ao carregar mais agentes:", error);
      setNotify({
        type: "danger",
        message: "Erro ao carregar mais agentes",
        title: "Erro",
        visible: true,
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasMoreAgents, isLoadingMore, isLoading, setNotify]);

  // Hook do scroll infinito
  const {
    containerRef,
    error: scrollError,
    retry,
  } = useInfiniteScroll({
    loadMore: loadMoreAgents,
    hasMore: hasMoreAgents,
    isLoading: isLoadingMore,
    threshold: 100,
    disabled: isLoading,
  });

  // Tratamento de erro do scroll infinito
  useEffect(() => {
    if (scrollError) {
      setNotify({
        type: "danger",
        message: "Erro no scroll infinito: " + scrollError,
        title: "Erro",
        visible: true,
      });
    }
  }, [scrollError, setNotify]);

  // Carrega os dados dos agentes
  const loadAgentsData = async () => {
    setIsLoading(true);
    try {
      const res = await GetAgentsData(1, itemsPerPage);
      setAgentsData(res || []);
      setFilteredAgents(res || []);
      setCurrentPage(1);
      setHasMoreAgents(res && res.length === itemsPerPage);
    } catch (err) {
      console.error("Erro ao carregar agentes:", err);
      setNotify({
        type: "danger",
        message: "Erro ao carregar agentes",
        title: "Erro",
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (bottyData?.token) {
      loadAgentsData();
    }
  }, [bottyData?.token, setNotify]);

  // Filter agents based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredAgents(showAgentsData);
    } else {
      const filtered = showAgentsData.filter(
        (agent) =>
          agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.agent_job?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, showAgentsData]);

  // Carrega as configurações de notificação
  const loadNotificationConfig = async () => {
    if (!bottyData?.uid) return;

    setLoadingNotifications(true);
    try {
      const data = await NotificationService.getCurrentConfig();

      // Garantir que notificationTypes está no formato correto
      if (data && !data.notificationTypes && data.notification_types) {
        // Converte notification_types para notificationTypes
        data.notificationTypes = data.notification_types.map((type) => ({
          value: type,
          label:
            type === "whatsapp"
              ? "Mensagem no WhatsApp"
              : "Mensagem na Plataforma",
        }));
      }

      setNotificationConfig(data);
    } catch (error) {
      console.error("Erro ao carregar configurações de notificação:", error);
      setNotify({
        type: "danger",
        message: "Erro ao carregar configurações de notificação",
        title: "Erro",
        visible: true,
      });
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Salva as configurações de notificação
  const handleSaveNotificationConfig = async (data) => {
    try {
      let result;

      if (data.id) {
        // Atualiza configuração existente
        result = await NotificationService.updateConfig(data.id, data);
      } else {
        // Cria nova configuração
        result = await NotificationService.saveConfig(data);
      }

      if (result.notificationTypes) {
        delete result.id;
        delete result.created_at;
        delete result.notificationTypes;
      }

      let notifications = {
        notifications: result,
      };
      localStorage.removeItem("_ACCSETTINGS_");
      localStorage.setItem("_ACCSETTINGS_", JSON.stringify(notifications));
      setNotify({
        title: "Sucesso!",
        type: "success",
        message: "Configurações de notificação salvas com sucesso!",
        visible: false,
      });

      // Garante que o resultado tenha notificationTypes no formato adequado
      if (result && !result.notificationTypes && result.notification_types) {
        result.notificationTypes = result.notification_types.map((type) => ({
          value: type,
          label:
            type === "whatsapp"
              ? "Mensagem no WhatsApp"
              : "Mensagem na Plataforma",
        }));
      }

      setNotificationConfig(result);
      setShowNotificationsModal(false);
    } catch (error) {
      console.error("Erro ao salvar configurações de notificação:", error);
      setNotify({
        title: "Oops!",
        type: "error",
        message: "Erro ao salvar configurações de notificação",
        visible: false,
      });
    }
  };

  // Abre o modal de notificações
  const handleOpenNotificationsModal = () => {
    loadNotificationConfig();
    setShowNotificationsModal(true);
  };

  // Função para editar agente - refatorada
  const handleEditAgent = (agent) => {
    console.log("Editando agente:", agent);

    // Validação dos dados do agente
    if (!agent) {
      setNotify({
        type: "warning",
        message: "Dados do agente não encontrados",
        title: "Atenção",
      });
      return;
    }

    navigate(`/agents/${agent.id}`, {
      state: {
        data: agent,
        isEditing: true,
      },
    });
  };

  // Função para criar novo agente
  const handleCreateAgent = () => {
    setSelectedAgentData(null);
    // setAiShowModal(true);
    navigate(`/agents/create`, {
      state: {
        data: null,
        isEditing: false,
      },
    });
  };

  // Função para abrir modal de API Key
  const handleOpenApiKeyModal = (agent) => {
    setSelectedAgentForApiKey(agent);
    setApiKeyValue(agent.api_key || "");
    setShowApiKeyModal(true);
  };

  const handleDeleteAgent = async (agent) => {
    if (agent.is_active) {
      console.log('uai')
      setNotify({
        type: "warning",
        message: "Este agente está ativo e funcionando em seu whatsapp. Desative-o antes de excluir.",
        title: "Atenção",
        visible: true,
      });
      return;
    }

    const result = await Swal.fire({
      title: "Excluir agente?",
      html: `Você tem certeza que deseja excluir o agente "${agent.agent_name}"? <b>Esta ação não pode ser desfeita!</b>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (result.isConfirmed) {
      try {
        await deleteAgent(agent.id); 
        setAgentsData((prev) => prev.filter((a) => a.id !== agent.id));
        setNotify({
          type: "success",
          message: `Agente "${agent.agent_name}" excluído com sucesso!`,
          title: "Sucesso",
          visible: true,
        });
      } catch (error) {
        console.error("Erro ao excluir agente:", error);
        setNotify({
          type: "danger",
          message: "Erro ao excluir agente",
          title: "Erro",
          visible: true,
        });
      }
    }
  }

  // Função para salvar API Key
  const handleSaveApiKey = async () => {
    if (!selectedAgentForApiKey || !apiKeyValue.trim()) {
      setNotify({
        type: "warning",
        message: "Por favor, insira uma API Key válida",
        title: "Atenção",
        visible: true,
      });
      return;
    }

    // Validação básica da API Key da OpenAI
    if (!apiKeyValue.trim().startsWith("sk-")) {
      setNotify({
        type: "warning",
        message: "API Key deve começar com 'sk-'",
        title: "Atenção",
        visible: true,
      });
      return;
    }

    setIsUpdatingApiKey(true);
    try {
      await UpdateAgentApiKey(selectedAgentForApiKey.id, apiKeyValue.trim());

      // Atualiza o agente na lista
      setAgentsData((prev) =>
        prev.map((agent) =>
          agent.id === selectedAgentForApiKey.id
            ? { ...agent, api_key: apiKeyValue.trim() }
            : agent
        )
      );

      setNotify({
        type: "success",
        message: "API Key atualizada com sucesso!",
        title: "Sucesso",
        visible: true,
      });

      setShowApiKeyModal(false);
      setSelectedAgentForApiKey(null);
      setApiKeyValue("");
    } catch (error) {
      console.error("Erro ao atualizar API Key:", error);
      setNotify({
        type: "danger",
        message: "Erro ao atualizar API Key",
        title: "Erro",
        visible: true,
      });
    } finally {
      setIsUpdatingApiKey(false);
    }
  };

  // Função para testar agente (abrir chat)
  const handleTestAgent = (agent) => {
    if (!agent.api_key || agent.api_key.trim() === "") {
      setNotify({
        type: "warning",
        message: "Configure a API Key antes de testar o agente",
        title: "Atenção",
        visible: true,
      });
      return;
    }

    // if (!agent.is_active) {
    //   setNotify({
    //     type: "warning",
    //     message: "Ative o agente antes de testá-lo",
    //     title: "Atenção",
    //   });
    //   return;
    // }

    setSelectedAgentForChat(agent);
    setShowAgentChat(true);
  };

  // Função para ativar/desativar agente
  const handleToggleAgentStatus = async (agent) => {
    const newStatus = !agent.is_active;
    const action = newStatus ? "ativar" : "desativar";

    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} agente?`,
      text: `Você tem certeza que deseja ${action} o agente "${agent.agent_name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: newStatus ? "#28a745" : "#dc3545",
    });

    if (result.isConfirmed) {
      setIsUpdatingStatus(true);
      try {
        await UpdateAgentStatus(agent.id, newStatus);

        // Atualiza o agente na lista
        setAgentsData((prev) =>
          prev.map((a) =>
            a.id === agent.id ? { ...a, is_active: newStatus } : a
          )
        );

        setNotify({
          type: "success",
          message: `Agente ${
            newStatus ? "ativado" : "desativado"
          } com sucesso!`,
          title: "Sucesso",
          visible: true,
        });
      } catch (error) {
        console.error("Erro ao atualizar status do agente:", error);
        setNotify({
          type: "danger",
          message: "Erro ao atualizar status do agente",
          title: "Erro",
          visible: true,
        });
      } finally {
        setIsUpdatingStatus(false);
      }
    }
  };

  // Função para alterar versão do agente
  const handleChangeVersion = async (agent, newVersion) => {
    if (newVersion === agent.version_active) return;

    const result = await Swal.fire({
      title: "Alterar versão do agente?",
      text: `Você tem certeza que deseja alterar para a versão ${newVersion}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, alterar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#007bff",
    });

    if (result.isConfirmed) {
      setIsUpdatingVersion(true);
      try {
        await UpdateAgentVersion(agent.id, newVersion);

        // Atualiza o agente na lista
        setAgentsData((prev) =>
          prev.map((a) =>
            a.id === agent.id ? { ...a, version_active: newVersion } : a
          )
        );

        setNotify({
          type: "success",
          message: `Versão alterada para v.${newVersion} com sucesso!`,
          title: "Sucesso",
          visible: true,
        });
      } catch (error) {
        console.error("Erro ao alterar versão do agente:", error);
        setNotify({
          type: "danger",
          message: "Erro ao alterar versão do agente",
          title: "Erro",
          visible: true,
        });
      } finally {
        setIsUpdatingVersion(false);
      }
    }
  };

  // Função para gerar opções de versão
  const generateVersionOptions = (agent) => {
    const versions = [];
    const maxVersion = agent.version || 1;

    for (let i = 1; i <= maxVersion; i++) {
      versions.push(i);
    }

    return versions.reverse().slice(0, 10);
  };

  const getStats = () => {
    const total = showAgentsData.length;
    const active = showAgentsData.filter((agent) => agent.is_active).length;
    const models = [...new Set(showAgentsData.map((agent) => agent.model))]
      .length;

    return { total, active, models };
  };

  const stats = getStats();

  if (isLoading) {
    return (
      <Container className="main-container">
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando agentes...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container className="main-container" ref={containerRef}>
      <Header>
        <Title>
          <TitleIcon>
            <IconRobot size={24} />
          </TitleIcon>
          Meus Agentes
        </Title>
      </Header>

      <SearchContainer>
        <SearchIcon>
          <IconSearch size={18} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Buscar agentes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <StatsContainer>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.active}</StatNumber>
          <StatLabel>Ativos</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.models}</StatNumber>
          <StatLabel>Modelos</StatLabel>
        </StatCard>
      </StatsContainer>

      <ActionsBar>
        <div></div>
        <NotificationButton onClick={handleOpenNotificationsModal}>
          <IconBellRinging size={18} />
          Notificações
        </NotificationButton>
      </ActionsBar>

      {filteredAgents.length === 0 ? (
        <EmptyState>
          <h3>
            {searchTerm
              ? "Nenhum agente encontrado"
              : "Nenhum agente configurado"}
          </h3>
          <p>
            {searchTerm
              ? "Tente ajustar sua busca para encontrar agentes."
              : "Comece configurando seu primeiro agente de IA para automatizar conversas."}
          </p>
        </EmptyState>
      ) : (
        <CardsContainer>
          {filteredAgents.map((agent, idx) => (
            <AgentCard key={agent.id || idx}>
              <CardContent>
                <div className="d-flex justify-content-between mb-4">
                  <AgentIconContainer>
                    <IconRobot size={28} />
                  </AgentIconContainer>

                  <Dropdown align="end">
                    <Dropdown.Toggle
                      as={CustomDropdownToggle}
                      id={`dropdown-agent-${idx}`}
                    >
                      <IconDotsVertical size={20} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu as={CustomDropdownMenu}>
                      <Dropdown.Item
                        as={CustomDropdownItem}
                        onClick={() => handleEditAgent(agent)}
                      >
                        <IconEdit size={16} />
                        Editar Agente
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={CustomDropdownItem}
                        onClick={() => handleOpenApiKeyModal(agent)}
                      >
                        <IconKey size={16} />
                        API Key
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={CustomDropdownItem}
                        onClick={() => handleToggleAgentStatus(agent)}
                        disabled={isUpdatingStatus}
                      >
                        {agent.is_active ? (
                          <>
                            <IconToggleRight size={16} />
                            Desativar
                          </>
                        ) : (
                          <>
                            <IconToggleLeft size={16} />
                            Ativar
                          </>
                        )}
                      </Dropdown.Item>
                      <Dropdown.Item
                        as={CustomDropdownItem}
                        onClick={() => handleDeleteAgent(agent)}
                      >
                        <IconTrash size={16} />
                        Excluir
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <AgentName>{agent.agent_name}</AgentName>
                <AgentJob>{agent.agent_job}</AgentJob>
                <Col md={12} className="d-flex justify-content-between px-0">
                  <ModelBadge>v.{agent.version_active}</ModelBadge>
                  {/* Seletor de versão */}
                  {/*  <Col md={6} sm={6}>
                    <VersionSelector
                      value={agent.version_active}
                      onChange={(e) =>
                        handleChangeVersion(agent, parseInt(e.target.value))
                      }
                      disabled={isUpdatingVersion}
                    >
                      {generateVersionOptions(agent).map((version) => (
                        <option key={version} value={version}>
                          v.{version}
                        </option>
                      ))}
                    </VersionSelector>
                  </Col> */}
                </Col>
              </CardContent>

              <CardFooter className="d-flex justify-content-between">
                <StatusIndicator>
                  <StatusDot
                    style={{
                      backgroundColor: agent.is_active ? "#28a745" : "#dc3545",
                    }}
                  />
                  {agent.is_active ? "Ativo" : "Inativo"}
                </StatusIndicator>
                <TestButton
                  onClick={() => handleTestAgent(agent)}
                  disabled={!agent.api_key}
                >
                  <IconMessageCircle size={14} />
                  Testar
                </TestButton>
              </CardFooter>
            </AgentCard>
          ))}
        </CardsContainer>
      )}

      {/* Loading para scroll infinito */}
      {isLoadingMore && (
        <LoadingWrapper>
          <Spinner animation="border" variant="primary" size="sm" />
          <span>Carregando mais agentes...</span>
        </LoadingWrapper>
      )}

      {/* Botão flutuante para adicionar agente */}
      <FloatingButton onClick={handleCreateAgent} title="Novo Agente">
        <IconPlus size={24} />
      </FloatingButton>

      {/* Modal do Agente */}
      {/* {showAiModal && (
        <Modal
          show={showAiModal}
          onHide={handleCloseAgentModal}
          keyboard={false}
          centered
          size="xl"
          className="pl-0"
        >
          <Modal.Header
            style={{
              position: "absolute",
              right: 0,
              zIndex: 10,
              border: "unset",
            }}
            closeButton
          ></Modal.Header>
          <Modal.Body className="p-0">
            <AIAgentWizard
              data={selectedAgentData}
              token={bottyData?.token}
              setNotify={setNotify}
              setShowModal={handleCloseAgentModal}
              onSave={handleAgentSaved}
              isEditing={Boolean(selectedAgentData?.isEditing)}
            />
          </Modal.Body>
        </Modal>
      )} */}

      {/* Modal de API Key */}
      <Modal
        show={showApiKeyModal}
        onHide={() => setShowApiKeyModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Configurar API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>API Key da OpenAI</Form.Label>
              <Form.Control
                type="password"
                placeholder="sk-..."
                value={apiKeyValue}
                onChange={(e) => setApiKeyValue(e.target.value)}
                disabled={isUpdatingApiKey}
              />
              <Form.Text className="text-muted">
                Esta chave será usada para as requisições da OpenAI.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApiKeyModal(false)}
            disabled={isUpdatingApiKey}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveApiKey}
            disabled={isUpdatingApiKey || !apiKeyValue.trim()}
          >
            {isUpdatingApiKey ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Notificações */}
      <Modal
        show={showNotificationsModal}
        onHide={() => setShowNotificationsModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Configurar Notificações</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingNotifications ? (
            <DefaultLoader isLoading={loadingNotifications} />
          ) : (
            <NotificationsModal
              initialData={notificationConfig}
              onSave={handleSaveNotificationConfig}
              onHide={() => setShowNotificationsModal(false)}
              show={showNotificationsModal}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* AgentChat */}
      {showAgentChat && selectedAgentForChat && (
        <AgentChat
          agentData={selectedAgentForChat}
          onClose={() => setShowAgentChat(false)}
        />
      )}
    </Container>
  );
}
